import {trackConversion, trackEvent} from '@js/actions/trackingActions';

import StorageService from '@js/modules/storage';


const AnalyticsService = (function () {
    const AnalyticsServiceModel = {};

    const SECURE_DISPATCH_TIMEOUT = 200; // in ms

    // Reset on every page load: see trackPaymentFormReady.
    const _paymentFormReadyReported = new Set();

    const _isMetricsAvailable = function () {
        return window.isMetricsEnabled;
    };

    const _setWithExpiry = function (key, value, ttl) {
        const now = new Date();

        const item = {
            value: value,
            expiry: now.getTime() + ttl,
        };

        StorageService.setItem(key, item);
    };

    const _getWithExpiry = function (key) {
        const item = StorageService.getItem(key);
        if (!item) {
            return null;
        }

        const now = new Date();
        // compare the expiry time of the item with the current time
        if (now.getTime() > item.expiry) {
            // If the item is expired, delete the item from storage and return null
            StorageService.removeItem(key);

            return null;
        }

        return item.value;
    };

    const _isUniqPerSession = function (eventName) {
        const eventInSession = _getWithExpiry(`analytics-${eventName}`);

        if (eventInSession) {
            return false;
        } else {
            // Sessions in Matomo last for 30 minutes
            _setWithExpiry(`analytics-${eventName}`, 'true', 1800000);

            return true;
        }
    };

    const _isUniqPerConversion = function (eventName) {
        const item = StorageService.getItem(`conversion-${eventName}`);
        if (item) {
            return false;
        } else {
            StorageService.setItem(`conversion-${eventName}`, 'true');

            return true;
        }
    };

    const _secureDispatch = function (eventName) {
        return new Promise((resolve) => {
            setTimeout(() => resolve(eventName), SECURE_DISPATCH_TIMEOUT);
        });
    };

    const _trackEvent = function (eventAction, eventName, eventValue, eventValueData) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['trackEvent', eventAction, eventName, eventValue, eventValueData].filter(Boolean));
    };

    const _trackSearch = function (query, resultCount, searchCategory = false) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['trackSiteSearch', query, searchCategory, resultCount]);
    };

    const _sendConversion = function (eventName) {
        if (!_isUniqPerConversion(eventName)) {
            return;
        }

        trackConversion({
            event_name: eventName
        });
    };

    const _sendEvent = function (name, properties = {}) {
        trackEvent(name, properties);
    };

    /* Initialization
    ******************** */
    AnalyticsServiceModel.initialize = function () {
        if (!_isMetricsAvailable()) {
            return false;
        }

        // Change how long a tab needs to be active to be counted as viewed in seconds
        // Requires a page to be actively viewed for 10 seconds for any heart beat request to be sent.
        window._paq.push(['enableHeartBeatTimer', 10]);

        return true;
    };

    /* Inscription and connection
    ******************** */
    AnalyticsServiceModel.trackSignupPage = function () {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', '/signup']);
        window._paq.push(['trackPageView', 'Signup']);
    };

    AnalyticsServiceModel.trackSignupSuccess = function (userId) {
        _trackEvent('Subscription', 'signup', userId);

        return _secureDispatch('SignupSuccess');
    };

    AnalyticsServiceModel.trackLoginPage = function () {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', '/login']);
        window._paq.push(['trackPageView', 'Login']);
    };

    AnalyticsServiceModel.trackLoginSuccess = function (userId) {
        _trackEvent('Subscription', 'login', userId);
    };

    AnalyticsServiceModel.trackSignupAttempt = function () {
        _sendEvent('signup_attempt');
    };

    AnalyticsServiceModel.trackLoginAttempt = function () {
        _sendEvent('login_attempt');
    };

    AnalyticsServiceModel.trackSignupValidationError = function (fields, reasons) {
        const flatten = (value) => (Array.isArray(value) ? value.join(',') : String(value || ''));

        // An unnamed failure is still a failure: sent blank, these landed in an empty bucket
        // in the admin, indistinguishable from one another and dropped from the reason panel.
        _sendEvent('signup_validation_error', {
            fields:  flatten(fields) || 'unknown',
            reasons: flatten(reasons) || 'unknown:unreported'
        });
    };

    AnalyticsServiceModel.trackOauthClick = function (provider) {
        _trackEvent('Subscription', 'OAuth click', provider);

        _sendEvent('oauth_click', {provider: provider || ''});
    };

    AnalyticsServiceModel.trackOauthPopupBlocked = function (provider) {
        _sendEvent('oauth_popup_blocked', {provider: provider || ''});
    };

    AnalyticsServiceModel.trackOauthSuccess = function (provider) {
        _trackEvent('Subscription', 'OAuth success', provider);

        _sendEvent('oauth_success', {provider: provider || ''});
    };

    /* Map tiles
    ******************** */
    AnalyticsServiceModel.trackMapStyleUsed = function (mapTileService) {
        if (!StorageService.getItem('map_usage_tile_service_tracked')) {
            _trackEvent('Map tiles', 'Tiles service', mapTileService);
            _sendEvent('map_style_used', {map_tile_service: mapTileService || ''});
            StorageService.setItem('map_usage_tile_service_tracked', 'true');
        }
    };

    AnalyticsServiceModel.trackMapStyleChanged = function (mapTileService) {
        _trackEvent('Map tiles', 'Map style changed', mapTileService);

        _sendConversion('map_style_changed');
    };

    // AnalyticsServiceModel.trackMapTilesCount = function (mapTileService, differenceTileIdsSize) {
    //     _trackEvent('Map tiles', 'Tiles loaded', mapTileService, differenceTileIdsSize);
    // };

    /* Map limitations
    ******************** */
    // AnalyticsServiceModel.trackMapTilesMaxReached = function (mapTileService) {
    //     _trackEvent('Map limitations', 'Max Tiles reached', mapTileService);
    // };

    AnalyticsServiceModel.trackMapDistanceMaxReached = function () {
        _trackEvent('Map limitations', 'Max distance reached');
    };

    /* Map stats
    ******************** */
    // AnalyticsServiceModel.trackMapDistanceComputed = function (deltaDistance) {
    //     _trackEvent('Map stats', 'Compute distance', 'Distance computed', deltaDistance);
    // };

    AnalyticsServiceModel.trackMapComputeDuration = function (rideType) {
        _trackEvent('Map stats', 'Compute duration', rideType);

        _sendConversion('compute_duration');
    };

    AnalyticsServiceModel.trackComputeElevationClick = function (pageType) {
        _trackEvent('Map stats', 'Compute elevation', pageType);

        _sendConversion('compute_elevation');
    };

    AnalyticsServiceModel.track3DViewClick = function () {
        _trackEvent('Map stats', '3D View');

        _sendConversion('3d_view');
    };

    AnalyticsServiceModel.trackSlopePercentageClick = function () {
        _trackEvent('Map stats', 'Slope percentage');

        _sendConversion('slope_percentage');
    };

    AnalyticsServiceModel.trackFreehandDrawClick = function () {
        _trackEvent('Map stats', 'Freehand draw');

        // Unique per visitor (persistent localStorage guard + Ahoy visitor dedup); surfaces as a
        // "Freehand Draw" row in the admin conversions actions funnel.
        _sendConversion('freehand_draw');
    };

    AnalyticsServiceModel.trackSurfaceTypeClick = function () {
        _trackEvent('Map stats', 'Surface type');

        _sendConversion('surface_type');
    };

    // Save-nudge popup (unconnected visitor trying advanced features on a drawn route). Each is
    // unique per visitor and surfaces automatically in the admin conversions funnel.
    AnalyticsServiceModel.trackSaveNudgeShown = function () {
        _trackEvent('Conversion', 'Save nudge shown');

        _sendConversion('save_nudge_shown');
    };

    AnalyticsServiceModel.trackSaveNudgeCta = function () {
        _trackEvent('Conversion', 'Save nudge CTA');

        _sendConversion('save_nudge_cta');
    };

    AnalyticsServiceModel.trackSaveNudgeDismissed = function () {
        _trackEvent('Conversion', 'Save nudge dismissed');

        _sendConversion('save_nudge_dismissed');
    };

    AnalyticsServiceModel.trackRouteReversed = function () {
        _trackEvent('Map stats', 'Route reversed');

        _sendConversion('route_reversed');
    };

    AnalyticsServiceModel.trackRouteOutAndBack = function () {
        _trackEvent('Map stats', 'Route out and back');

        _sendConversion('route_out_and_back');
    };

    AnalyticsServiceModel.trackWaypointPauseSet = function () {
        _trackEvent('Map stats', 'Waypoint pause set');

        _sendConversion('waypoint_pause_set');
    };

    AnalyticsServiceModel.trackSectionColorSet = function () {
        _trackEvent('Map stats', 'Section color set');

        _sendConversion('section_color_set');
    };

    // New map features — each surfaces as a unique-per-visitor row in the admin conversions actions
    // funnel (localStorage guard + Ahoy visitor dedup), so we measure adoption, not clicks.
    AnalyticsServiceModel.trackWaypointModeUsed = function () {
        _trackEvent('Map stats', 'Waypoint mode');

        _sendConversion('waypoint_mode_used');
    };

    AnalyticsServiceModel.trackImagePoiModeUsed = function () {
        _trackEvent('Map stats', 'Image POI mode');

        _sendConversion('image_poi_mode_used');
    };

    AnalyticsServiceModel.trackPoiSuggestionsUsed = function () {
        _trackEvent('Map stats', 'POI suggestions');

        _sendConversion('poi_suggestions_used');
    };

    // The trigger above says the tool was used; this says a suggestion was actually turned into a
    // waypoint. Without it the suggested/added ratio — whether the map preview solved anything —
    // stays blind.
    AnalyticsServiceModel.trackPoiSuggestionAdded = function () {
        _trackEvent('Map stats', 'POI suggestion added');

        _sendConversion('poi_suggestion_added');
    };

    AnalyticsServiceModel.trackMultiDayGenerated = function () {
        _trackEvent('Map stats', 'Multi-day stages');

        _sendConversion('multi_day_generated');
    };

    AnalyticsServiceModel.trackFlyoverPlayed = function () {
        _trackEvent('Map stats', '3D fly-over');

        _sendConversion('flyover_played');
    };

    AnalyticsServiceModel.trackRetraceUsed = function () {
        _trackEvent('Map stats', 'Retrace');

        _sendConversion('retrace_used');
    };

    AnalyticsServiceModel.trackRouteTypeUsed = function () {
        _trackEvent('Map stats', 'Route type');

        _sendConversion('route_type_used');
    };

    AnalyticsServiceModel.trackHighlightUsed = function () {
        _trackEvent('Map stats', 'Highlight elements');

        _sendConversion('highlight_used');
    };

    AnalyticsServiceModel.trackTcxExported = function () {
        _trackEvent('Map stats', 'TCX export');

        _sendConversion('ride_exported_tcx');
    };

    // Round-trip loop generation. `type` is 'distance' | 'duration', `value` the requested amount
    // (meters or seconds). Persisted with its properties for the admin conversions dashboard.
    AnalyticsServiceModel.trackRoundTripGenerated = function (type, value) {
        _trackEvent('Map stats', 'Round trip', type, value);

        _sendEvent('round_trip_generated', {
            type: type || '',
            value: String(value || '')
        });
    };

    AnalyticsServiceModel.trackIsochroneSelected = function (type, value) {
        _trackEvent('Map stats', 'Isochrone selected', type, value);

        _sendEvent('isochrone_selected', {
            type: type || '',
            value: String(value || '')
        });
    };

    AnalyticsServiceModel.trackIsochroneComputed = function (type, value) {
        _trackEvent('Map stats', 'Isochrone computed', type, value);

        _sendEvent('isochrone_computed', {
            type: type || '',
            value: String(value || '')
        });
    };

    AnalyticsServiceModel.trackGeolocationClick = function (pageType, rideName, rideId) {
        if (pageType === 'Ride show') {
            if (_isUniqPerSession(`geolocation-ride-${rideId}`)) {
                _trackEvent('Geolocation', 'Locate', `${rideName} (${rideId})`);
            }
        } else if (pageType === 'Search') {
            _trackEvent('Geolocation', 'Locate', 'Search page');
        } else if (pageType === 'Home page') {
            _trackEvent('Geolocation', 'Locate', 'Home page');
        }

        _sendEvent('geolocation_used');
    };

    AnalyticsServiceModel.trackGeotrackingClick = function (pageType, rideName, rideId) {
        if (pageType === 'Ride show') {
            if (_isUniqPerSession(`geotracking-ride-${rideId}`)) {
                _trackEvent('Geolocation', 'Tracking', `${rideName} (${rideId})`);
            }
        } else if (pageType === 'Explore page') {
            if (_isUniqPerSession('geotracking-explore')) {
                _trackEvent('Geolocation', 'Tracking', 'Explore page');
            }
        }

        _sendEvent('geotracking_used');
    };

    /* Home page
    ******************** */
    AnalyticsServiceModel.trackHomeShowMapClick = function () {
        if (_isUniqPerSession('home-map-displayed')) {
            _trackEvent('Home', 'Home - Show map');
        }

        _sendEvent('home_show_map');
    };

    AnalyticsServiceModel.trackHomeShowPlaceSelected = function () {
        if (_isUniqPerSession('home-place-selected')) {
            _trackEvent('Home', 'Home - Place selected');
        }

        _sendConversion('place_selected');
    };

    AnalyticsServiceModel.trackHomePathStarted = function () {
        if (_isUniqPerSession('home-path-started')) {
            _trackEvent('Home', 'Home - Path started');
        }

        _sendConversion('path_started');
    };

    AnalyticsServiceModel.trackHomeShowFormClick = function () {
        if (_isUniqPerSession('home-form-displayed')) {
            _trackEvent('Home', 'Home - Show form');
        }

        _sendConversion('ride_show_form');
    };

    AnalyticsServiceModel.trackPaceProfileSelected = function (profile, rideType) {
        _trackEvent('Ride', 'Pace profile', profile);

        _sendEvent('pace_profile_selected', {
            profile: profile || '',
            ride_type: rideType || ''
        });
    };

    AnalyticsServiceModel.trackUnitManualOverride = function (from, to) {
        _sendEvent('unit_manual_override', {
            from: from || '',
            to: to || ''
        });
    };

    AnalyticsServiceModel.trackHomeRideSaved = function () {
        if (_isUniqPerSession('home-ride-saved')) {
            _trackEvent('Home', 'Home - Ride saved');
        }

        _sendConversion('ride_saved');
    };

    /* Save-flow account funnel (home + planner share RideCreation)
    ******************** */
    // A required field blocked the save form (distance/duration/speed/name).
    AnalyticsServiceModel.trackRideFormValidationError = function (field) {
        _sendEvent('ride_form_validation_error', {field: field || ''});
    };

    // The "create an account to save" modal was shown to a non-connected user.
    AnalyticsServiceModel.trackAccountModalShown = function () {
        _sendConversion('account_modal_shown');
    };

    // The account modal was closed without connecting (save abandoned at the wall).
    AnalyticsServiceModel.trackAccountModalDismissed = function () {
        _sendConversion('account_modal_dismissed');
    };

    /* Account-gate modal (unified "create a free account" wall)
    ******************** */
    // The account modal was shown, carrying the feature that triggered it. Keeps the
    // save-funnel `account_modal_shown` flag populated and adds a per-feature event.
    AnalyticsServiceModel.trackAccountGateShown = function (feature) {
        _sendEvent('account_gate_shown', {feature: feature || ''});

        _sendConversion('account_modal_shown');
    };

    // The visitor engaged with the account modal (submitted their email or clicked an
    // OAuth button) — the "clicked" step between shown and converted. `method` is
    // 'email' | 'oauth'. Unique per visitor for the funnel, plus a per-feature event.
    AnalyticsServiceModel.trackAccountGateAttempt = function (feature, method) {
        _sendEvent('account_gate_attempt', {feature: feature || '', method: method || ''});

        _sendConversion('account_gate_attempt');
    };

    // A signup/login completed through the account modal (unique per visitor): the visitor
    // actually became a connected user. Slots into the save funnel as "signup completed",
    // plus a per-feature event.
    AnalyticsServiceModel.trackAccountGateSignup = function (feature) {
        _sendEvent('account_gate_signup', {feature: feature || ''});

        _sendConversion('account_gate_signup');
    };

    // The account modal was dismissed without connecting.
    AnalyticsServiceModel.trackAccountGateDismissed = function () {
        _sendConversion('account_modal_dismissed');
    };

    /* Planner page
    ******************** */
    AnalyticsServiceModel.trackPlannerShowMap = function () {
        _trackEvent('Planner', 'Planner - Show map used');

        _sendEvent('planner_show_map');
    };

    AnalyticsServiceModel.trackPlannerGeolocation = function () {
        _trackEvent('Planner', 'Planner - Geolocation used');

        _sendEvent('planner_geolocation');
    };

    AnalyticsServiceModel.trackPlannerPlaceSelected = function () {
        _trackEvent('Planner', 'Planner - Place used');

        _sendConversion('place_selected');
    };

    AnalyticsServiceModel.trackPlannerPathStarted = function () {
        if (_isUniqPerSession('planner-path-started')) {
            _trackEvent('Planner', 'Planner - Path started');
        }

        _sendConversion('path_started');
    };

    AnalyticsServiceModel.trackPlannerRideSaved = function () {
        if (_isUniqPerSession('planner-ride-saved')) {
            _trackEvent('Planner', 'Planner - Ride saved');
        }

        _sendConversion('ride_saved');
    };

    /* Ride
    ******************** */
    // AnalyticsServiceModel.trackSaveRide = function (pageType) {
    //     _trackEvent('Ride', 'Save ride', pageType);
    // };

    AnalyticsServiceModel.trackRideSaved = function (pageType) {
        if (pageType === 'Home page') {
            _trackEvent('Ride', 'Ride saved', pageType);
        } else if (pageType === 'Plan page') {
            _trackEvent('Ride', 'Ride saved', pageType);
        }
    };

    AnalyticsServiceModel.trackShowElevationClick = function (rideName, rideId) {
        _trackEvent('Ride', 'Show elevation', `${rideName} (${rideId})`);

        _sendEvent('show_elevation');
    };

    AnalyticsServiceModel.trackRouteImported = function (name, duration, coordinatesSize) {
        if (_isUniqPerSession(`track-imported-${name}`)) {
            _trackEvent('Ride', 'Track imported', `${name} - ${duration} (${coordinatesSize} points)`);
        }

        _sendConversion('track_imported');
    };

    AnalyticsServiceModel.trackRouteExported = function (rideId, rideName) {
        if (_isUniqPerSession(`track-exported-${rideId}`)) {
            _trackEvent('Ride', 'Track exported', rideId && rideName ? `${rideName} (${rideId})` : 'Direct export');
        }

        _sendConversion('track_exported');
    };

    // AnalyticsServiceModel.trackCreateRideStep = function (step) {
    //     _trackEvent('Plan ride', 'Steps', step);
    // };

    /* Ride share
    ******************** */
    AnalyticsServiceModel.trackShareRideModalShow = function (pageType) {
        _trackEvent('Ride Share', 'Show modal', pageType);
    };

    AnalyticsServiceModel.trackCollectionShared = function () {
        _trackEvent('Favorites', 'Collection shared');

        _sendEvent('collection_shared');
    };

    AnalyticsServiceModel.trackHomeRideShareSaved = function () {
        if (_isUniqPerSession('home-ride-share-saved')) {
            _trackEvent('Home', 'Home - Ride share saved');
        }
    };

    AnalyticsServiceModel.trackRideShared = function (tool) {
        _trackEvent('Ride Share', 'Shared', tool);

        _sendConversion('ride_shared');
    };

    AnalyticsServiceModel.trackPrivateRideShared = function (rideId) {
        _trackEvent('Ride Share', 'Private shared', rideId);

        _sendConversion('ride_private_shared');
    };

    /* Weather
    ******************** */
    AnalyticsServiceModel.trackWeatherClick = function (pageType) {
        _trackEvent('Map stats', 'Show weather', pageType);

        _sendConversion('show_weather');
    };

    /* Daylight
    ******************** */
    AnalyticsServiceModel.trackDaylightClick = function (pageType) {
        _trackEvent('Map stats', 'Show daylight', pageType);

        _sendConversion('show_daylight');
    };

    /* Time To Start
    ******************** */
    AnalyticsServiceModel.trackTimeToStartModalShow = function () {
        _trackEvent('Time To Start', 'Show modal');

        _sendConversion('time_to_start_show');
    };

    AnalyticsServiceModel.trackTimeToStartCompute = function () {
        _trackEvent('Time To Start', 'Compute distance');

        _sendEvent('time_to_start_compute');
    };

    /* Search
    ******************** */
    AnalyticsServiceModel.trackSearch = function (pageType, query, location, resultCount) {
        _trackSearch([query, location].filter(Boolean).join(' / '), resultCount, pageType);
    };

    /* E-commerce
    ******************** */
    AnalyticsServiceModel.trackProductModalShow = function (requiredFunctionality) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', '/product-subscription-modal']);
        window._paq.push(['trackPageView', 'Product subscription modal']);

        _trackEvent('Checkout', 'Product modal', requiredFunctionality);
    };

    AnalyticsServiceModel.trackProductModalDismissed = function (dismissType, functionality, timeToDismissMs) {
        _trackEvent('Checkout', 'Product modal dismissed', dismissType);

        _sendEvent('product_modal_dismissed', {
            dismiss_type: dismissType,
            functionality: functionality || '',
            time_to_dismiss_ms: (timeToDismissMs !== undefined && timeToDismissMs !== null) ? String(timeToDismissMs) : ''
        });
    };

    AnalyticsServiceModel.trackProductModalCtaClick = function (cta, functionality) {
        _trackEvent('Checkout', 'Product modal CTA', cta);

        _sendEvent('product_modal_cta_click', {
            cta: cta,
            functionality: functionality || ''
        });
    };

    AnalyticsServiceModel.trackProductModalSuppressed = function (functionality) {
        _sendEvent('product_modal_suppressed', {
            functionality: functionality || ''
        });
    };

    AnalyticsServiceModel.trackPaywallSoftNudge = function (functionality) {
        _sendEvent('paywall_soft_nudge_shown', {
            functionality: functionality || ''
        });
    };

    AnalyticsServiceModel.trackPaywallInlineAuth = function (method) {
        _trackEvent('Checkout', 'Paywall inline auth', method);

        _sendEvent('paywall_inline_auth', {method: method || ''});
    };

    AnalyticsServiceModel.trackPaywallAutoBasket = function (functionality) {
        _sendEvent('paywall_auto_basket', {functionality: functionality || ''});
    };

    // `details` carries the extra properties a given action needs: `from` on a shown (which step
    // the tour came from — a shown whose `from` is not the previous step means the tour resumed
    // mid-walk from storage, which the funnel used to read as a progression), and `points` on an
    // automatic dismissal (how many waypoints triggered it).
    AnalyticsServiceModel.trackOnboardingStep = function (step, action, details = {}) {
        _sendEvent('onboarding_step', {
            step:   step || '',
            action: action || '',
            ...details
        });
    };

    AnalyticsServiceModel.trackProductView = function (productId, productName, productPrice) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setEcommerceView', productId, productName, undefined, productPrice]);
        window._paq.push(['trackPageView']);
    };

    AnalyticsServiceModel.trackAddToBasket = function (productId, productName, productPrice) {
        if (!_isMetricsAvailable()) {
            return Promise.resolve();
        }

        window._paq.push(['addEcommerceItem', productId, productName, undefined, productPrice, 1]);
        window._paq.push(['trackEcommerceCartUpdate', productPrice]);

        if (_isUniqPerSession('add-to-basket')) {
            _trackEvent('Checkout', 'Add to basket');
        }

        return _secureDispatch('AddToBasket');
    };

    AnalyticsServiceModel.trackCheckoutPage = function (productId, productName, productPrice) {
        if (!_isMetricsAvailable()) {
            return Promise.resolve();
        }

        window._paq.push(['addEcommerceItem', productId, productName, undefined, productPrice, 1]);

        if (_isUniqPerSession('order-attempt')) {
            _trackEvent('Checkout', 'Order attempt');
        }

        return _secureDispatch('OrderAttempt');
    };

    AnalyticsServiceModel.trackCheckoutPageView = function () {
        _trackEvent('Checkout', 'Checkout page view');

        _sendEvent('checkout_page_view');
    };

    AnalyticsServiceModel.trackPaymentMethodSelected = function (method) {
        _trackEvent('Checkout', 'Payment method selected', method);

        _sendEvent('payment_method_selected', {method: method || 'unknown'});
    };

    // Guest submitted their email on the checkout page (guest checkout).
    AnalyticsServiceModel.trackCheckoutGuestSubmitted = function () {
        _trackEvent('Checkout', 'Guest email submitted');

        _sendEvent('checkout_guest_submitted');
    };

    AnalyticsServiceModel.trackPaymentFormReady = function (tool, methods) {
        const key = tool || '';

        if (_paymentFormReadyReported.has(key)) {
            return;
        }

        _paymentFormReadyReported.add(key);

        _sendEvent('payment_form_ready', {
            tool: key,
            // "The express element was ready and offered NO wallet" is a fact, not a missing
            // value — sent as '' it was dropped by the admin's `methods <> ''` filter, which
            // is why the wallet panel was reading 16 of 188 express events. A call that
            // carries no list at all (tool 'stripe'/'paypal') still sends nothing, since the
            // dimension does not apply to it.
            methods: Array.isArray(methods) ? (methods.join(',') || 'none') : String(methods || '')
        });
    };

    AnalyticsServiceModel.trackOrderSuccess = function (orderId, orderTotal, orderWithDiscount = false) {
        if (!_isMetricsAvailable()) {
            return Promise.resolve();
        }

        window._paq.push(['trackEcommerceOrder', orderId, orderTotal, undefined, undefined, undefined, orderWithDiscount]);

        if (_isUniqPerSession('order-success')) {
            _trackEvent('Checkout', 'Order success');
        }

        return _secureDispatch('OrderSuccess');
    };

    /* Other pages
    ******************** */
    AnalyticsServiceModel.trackRidePlannedPage = function (rideId) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', `/rides/${rideId}/planned`]);
        window._paq.push(['trackPageView', `Ride planned (${rideId})`]);
    };

    AnalyticsServiceModel.trackRidePerformancePage = function (rideId) {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', `/rides/${rideId}/performance`]);
        window._paq.push(['trackPageView', `Ride performance (${rideId})`]);
    };

    AnalyticsServiceModel.trackRidePoiAssociationPage = function () {
        if (!_isMetricsAvailable()) {
            return;
        }

        window._paq.push(['setCustomUrl', '/pois/ride-association']);
        window._paq.push(['trackPageView', 'Associate ride to POI']);
    };

    /* PWA
    ******************** */
    AnalyticsServiceModel.trackPWAMode = function (mode) {
        if (_isUniqPerSession('pwa-mode')) {
            _trackEvent('PWA', 'PWA mode', mode);
        }
    };

    AnalyticsServiceModel.trackPWAInstalled = function () {
        _trackEvent('PWA', 'PWA installed');

        _sendConversion('pwa_installed');
    };

    AnalyticsServiceModel.trackPWAPrompt = function () {
        _trackEvent('PWA', 'PWA install requested');
    };

    /* API and browser errors
    ******************** */
    // AnalyticsServiceModel.trackStorageError = function () {
    //     if (_isUniqPerSession('storage-error')) {
    //         _trackEvent('Error', 'Local storage unavailable');
    //     }
    // };

    AnalyticsServiceModel.trackWebGLError = function () {
        if (_isUniqPerSession('webgl-error')) {
            _trackEvent('Error', 'WebGL unavailable');
        }
    };

    AnalyticsServiceModel.trackLocationSearchError = function (errors) {
        _trackEvent('Error', 'Location search', errors);
    };

    AnalyticsServiceModel.trackRoutingError = function (errors) {
        _trackEvent('Error', 'Routing', errors);
    };

    /* Feature availability
    ******************** */

    // status: 'ok' | 'unsupported' (the WebGL2 probe says no) | 'crashed' (the MapLibre constructor threw) | 'stuck' (built, but the first render never happened — load watchdog)
    AnalyticsServiceModel.trackMapRender = function (status) {
        if (!_isUniqPerSession(`map-render-${status}`)) {
            return;
        }

        _sendEvent('map_render', {status});
    };

    // status: 'ok' | 'server_error' | 'timeout' | 'offline' | 'no_route' | 'quota' | 'exception'.
    // Every status but 'ok' leaves the user with a straight line instead of a road, unless `rescued`:
    // our failure whose segment Mapbox drew anyway.
    AnalyticsServiceModel.trackRoutingOutcome = function (status, service, {rescued = false} = {}) {
        if (!_isUniqPerSession(`routing-outcome-${status}${rescued ? '-rescued' : ''}`)) {
            return;
        }

        _sendEvent('routing_outcome', {status, service: service || 'unknown', ...(rescued ? {rescued: true} : {})});
    };

    AnalyticsServiceModel.trackIsochroneError = function (errors) {
        _trackEvent('Error', 'Isochrone', errors);
    };

    AnalyticsServiceModel.trackElevationError = function (errors) {
        _trackEvent('Error', 'Elevation', errors);
    };

    return AnalyticsServiceModel;
})();

export default AnalyticsService;
